"use client";

import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import {
	RiBugLine,
	RiDiscLine,
	RiLifebuoyLine,
	RiLightbulbLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { Spinner } from "@workspace/ui/components/spinner";
import { Textarea } from "@workspace/ui/components/textarea";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { trpc } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";
import { optionalEmailSchema } from "@/lib/validations/email";

// Form validation schema
const feedbackFormSchema = z.object({
	feedbackType: z.enum(["bug", "feature", "question", "other"]),
	description: z
		.string()
		.min(1, "Description is required")
		.max(1000, "Description is too long"),
	email: optionalEmailSchema,
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

interface SupportModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialType?: "bug" | "feature" | "question" | "other";
}

type FeedbackType = "bug" | "feature" | "question" | "other";

const feedbackTypes = [
	{
		value: "question" as FeedbackType,
		label: "Help",
		icon: RiLifebuoyLine,
	},
	{ value: "bug" as FeedbackType, label: "Bug Report", icon: RiBugLine },
	{
		value: "feature" as FeedbackType,
		label: "Feature Request",
		icon: RiLightbulbLine,
	},
	{ value: "other" as FeedbackType, label: "Other", icon: RiDiscLine },
];

export const SupportModal = memo<SupportModalProps>(
	({ isOpen, onClose, initialType = "question" }) => {
		const sendFeedbackMutation = trpc.feedback.send.useMutation({
			onSuccess: () => {
				toast({
					title: "Feedback sent!",
					description:
						"Thank you for your feedback. We'll review it and get back to you if needed.",
				});
				// Close modal on success
				onClose();
			},
			onError: (error) => {
				toast({
					title: "Error",
					description: `Failed to send feedback. Please try again. ${error.message}`,
					variant: "destructive",
				});
			},
		});

		const form = useForm<FeedbackFormValues>({
			resolver: standardSchemaResolver(feedbackFormSchema),
			defaultValues: {
				feedbackType: initialType,
				description: "",
				email: "",
			},
		});

		// Reset form when modal opens
		useEffect(() => {
			if (isOpen) {
				form.reset({
					feedbackType: initialType,
					description: "",
					email: "",
				});
			}
		}, [isOpen, form, initialType]);

		// Memoized submit handler for performance
		const handleSubmit = useCallback(
			async (data: FeedbackFormValues) => {
				const selectedType = feedbackTypes.find(
					(type) => type.value === data.feedbackType,
				);

				// Send feedback via tRPC mutation
				sendFeedbackMutation.mutate({
					feedbackType: selectedType?.label || data.feedbackType,
					description: data.description,
					userEmail: data.email || "",
				});
			},
			[sendFeedbackMutation],
		);

		return (
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
					<DialogTitle className="sr-only">Contact Support</DialogTitle>
					<DialogDescription className="sr-only">
						Get help with Humble or share your feedback with our team.
					</DialogDescription>
					<DialogHeader className="px-6 pt-6 pb-0">
						<h2 className="font-semibold leading-none tracking-tight">
							Contact Support
						</h2>
						<p className="text-muted-foreground text-sm">
							Get help with Humble or share your feedback with our team.
						</p>
					</DialogHeader>

					<div className="px-6 pb-6">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleSubmit)}
								className="space-y-6"
							>
								{/* Topic Selection */}
								<FormField
									control={form.control}
									name="feedbackType"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Topic</FormLabel>
											<div className="grid grid-cols-2 gap-3">
												{feedbackTypes.map((type) => (
													<button
														key={type.value}
														type="button"
														onClick={() => field.onChange(type.value)}
														className={cn(
															"flex items-center gap-2 rounded-lg border-2 p-3 text-left transition-colors",
															field.value === type.value
																? "border-primary bg-primary/5"
																: "border-muted bg-background hover:bg-accent hover:text-accent-foreground",
														)}
													>
														<type.icon className="h-4 w-4" />
														<span className="text-sm font-medium">
															{type.label}
														</span>
													</button>
												))}
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Optional Email */}
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Email Address</FormLabel>
											<FormControl>
												<Input
													placeholder="email@example.com (optional)"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Description */}
								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Description</FormLabel>
											<FormControl>
												<Textarea
													placeholder="Please provide details about your feedback..."
													rows={4}
													maxLength={1000}
													{...field}
												/>
											</FormControl>
											<div className="text-right text-muted-foreground text-xs">
												{(field.value ?? "").length}/1000
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								<DialogFooter>
									<div className="flex w-full items-center justify-between">
										<div /> {/* Spacer for alignment */}
										<div className="flex gap-2">
											<Button variant="ghost" onClick={onClose} type="button">
												Cancel
											</Button>
											<Button
												type="submit"
												disabled={sendFeedbackMutation.isPending}
											>
												{sendFeedbackMutation.isPending ? (
													<div className="flex items-center gap-2">
														<Spinner size="sm" variant="current" />
														<span>Sending...</span>
													</div>
												) : (
													"Send"
												)}
											</Button>
										</div>
									</div>
								</DialogFooter>
							</form>
						</Form>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);

SupportModal.displayName = "SupportModal";
